/**
 * Visitor Pass (web) — resident-side gate pass generation, mirroring the
 * mobile owner flow (`mobile/src/features/visitors/InviteQrSheet.tsx` +
 * `(owner)/visitors.tsx`). A resident creates a pre-approval pass for their
 * villa; the server returns a signed QR `code` plus a 6-char `short_code`
 * fallback. The guard scans the QR (or types the code) at the gate to let the
 * guest in — no live approval needed.
 *
 * Scope: pass creation, display (QR + short code), listing, and revoke. The
 * gate-side scan and the walk-in approve/deny flow live on mobile (guard app).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  QrCode,
  Plus,
  Share2,
  Copy,
  Check,
  Trash2,
  ShieldCheck,
} from "lucide-react";

import { Button, Card, Dialog, Input, Pill, EmptyState, toast } from "../ui";
import { visitorsApi } from "./api";
import "./VisitorPassPage.css";

const STATUS_PILL = {
  ACTIVE: { variant: "success", label: "Active" },
  EXHAUSTED: { variant: "neutral", label: "Used up" },
  REVOKED: { variant: "danger", label: "Revoked" },
  EXPIRED: { variant: "warning", label: "Expired" },
};

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shareMessage(inv) {
  return (
    `You're invited to ${inv.villa_number}, Vishal Sanjivini.\n` +
    `Show this code at the gate: ${inv.short_code}\n` +
    `Valid ${fmtDateTime(inv.valid_from)} – ${fmtDateTime(inv.valid_to)}.`
  );
}

export default function VisitorPassPage({ embedded = false }) {
  const [villas, setVillas] = useState(null); // null = loading
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [passInvite, setPassInvite] = useState(null); // invite shown in QR dialog
  const [tab, setTab] = useState("active"); // active | expired

  const reload = useCallback(async (signal) => {
    try {
      const [v, list] = await Promise.all([
        visitorsApi.myVillas({ signal }),
        visitorsApi.listInvites({ signal }),
      ]);
      setVillas(v);
      setInvites(list || []);
      setError("");
    } catch (e) {
      if (e.name === "AbortError") return;
      setError(e.message || "Couldn't load your visitor passes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    reload(ctrl.signal);
    return () => ctrl.abort();
  }, [reload]);

  const onCreated = useCallback((inv) => {
    setInvites((prev) => [inv, ...prev]);
    setFormOpen(false);
    setPassInvite(inv);
  }, []);

  const revoke = useCallback(async (inv) => {
    try {
      const updated = await visitorsApi.revokeInvite(inv.id);
      setInvites((prev) => prev.map((i) => (i.id === inv.id ? updated : i)));
      toast.success("Pass revoked.");
    } catch (e) {
      toast.error(e.message || "Couldn't revoke the pass.");
    }
  }, []);

  const noVilla = villas !== null && villas.length === 0;

  // Active = currently usable; Expired = everything past (expired / used up
  // / revoked).
  const activeCount = useMemo(
    () => invites.filter((i) => i.status === "ACTIVE").length,
    [invites]
  );
  const filtered = useMemo(
    () =>
      invites.filter((i) =>
        tab === "active" ? i.status === "ACTIVE" : i.status !== "ACTIVE"
      ),
    [invites, tab]
  );

  return (
    <div className="vsvp">
      <div className="vsa-page-head">
        {!embedded && (
          <div>
            <h1>Visitor Pass</h1>
            <p className="vsa-page-subtitle">
              Generate a QR pass for your guests. They show it at the gate — no
              call needed.
            </p>
          </div>
        )}
        <Button
          variant="primary"
          leftIcon={<Plus size={16} />}
          disabled={noVilla}
          onClick={() => setFormOpen(true)}
        >
          New pass
        </Button>
      </div>

      {error ? <div className="vsvp-error">{error}</div> : null}

      {!noVilla && !loading && invites.length > 0 ? (
        <div className="vsvp-tabs" role="tablist">
          <button
            className={`vsvp-tab ${tab === "active" ? "active" : ""}`}
            onClick={() => setTab("active")}
          >
            Active
            <span className="vsvp-tab-count">{activeCount}</span>
          </button>
          <button
            className={`vsvp-tab ${tab === "expired" ? "active" : ""}`}
            onClick={() => setTab("expired")}
          >
            Expired
            <span className="vsvp-tab-count">{invites.length - activeCount}</span>
          </button>
        </div>
      ) : null}

      {noVilla ? (
        <EmptyState
          icon={<ShieldCheck size={28} />}
          title="No villa linked to your profile yet"
          subtitle="Complete your villa membership before you can create visitor passes."
        />
      ) : loading ? (
        <div className="vsvp-loading">Loading…</div>
      ) : invites.length === 0 ? (
        <EmptyState
          icon={<QrCode size={28} />}
          title="No passes yet"
          subtitle="Create a pass for an expected guest, delivery, or cab and share the QR or code."
          cta={
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => setFormOpen(true)}
            >
              New pass
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<QrCode size={28} />}
          title={tab === "active" ? "No active passes" : "No expired passes"}
          subtitle={
            tab === "active"
              ? "Create a pass for an expected guest, delivery, or cab."
              : "Passes that expire, get used up, or are revoked show up here."
          }
        />
      ) : (
        <div className="vsvp-list">
          {filtered.map((inv) => {
            const sp = STATUS_PILL[inv.status] || {
              variant: "neutral",
              label: inv.status,
            };
            const active = inv.status === "ACTIVE";
            return (
              <Card key={inv.id} className="vsvp-card">
                <div className="vsvp-card-top">
                  <div className="vsvp-card-main">
                    <div className="vsvp-card-name">{inv.guest_name}</div>
                    <div className="vsvp-card-meta">
                      {inv.villa_number} ·{" "}
                      {inv.max_uses > 1
                        ? `${inv.use_count}/${inv.max_uses} entries`
                        : "single entry"}
                    </div>
                    <div className="vsvp-card-validity">
                      {fmtDateTime(inv.valid_from)} – {fmtDateTime(inv.valid_to)}
                    </div>
                  </div>
                  <Pill variant={sp.variant}>{sp.label}</Pill>
                </div>
                <div className="vsvp-card-actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<QrCode size={15} />}
                    onClick={() => setPassInvite(inv)}
                  >
                    Show QR
                  </Button>
                  {active ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Trash2 size={15} />}
                      onClick={() => revoke(inv)}
                    >
                      Revoke
                    </Button>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <CreatePassDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        villas={villas || []}
        onCreated={onCreated}
      />

      <PassDialog
        invite={passInvite}
        onClose={() => setPassInvite(null)}
      />
    </div>
  );
}

function CreatePassDialog({ open, onOpenChange, villas, onCreated }) {
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [villaId, setVillaId] = useState("");
  const [kind, setKind] = useState("GUEST");
  const [multi, setMulti] = useState(false);
  const [maxUses, setMaxUses] = useState("5");
  const [submitting, setSubmitting] = useState(false);

  // Default the villa to the resident's (only) villa when the dialog opens.
  useEffect(() => {
    if (open) {
      setVillaId(villas.length === 1 ? villas[0].id : "");
    } else {
      setGuestName("");
      setGuestPhone("");
      setKind("GUEST");
      setMulti(false);
      setMaxUses("5");
      setSubmitting(false);
    }
  }, [open, villas]);

  const canSubmit = guestName.trim().length > 0 && !!villaId && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const uses = multi ? Math.max(2, Math.min(100, Number(maxUses) || 5)) : 1;
      const inv = await visitorsApi.createInvite({
        villa_id: villaId,
        guest_name: guestName.trim(),
        guest_phone: guestPhone.trim() || undefined,
        kind,
        max_uses: uses,
      });
      toast.success("Pass created.");
      onCreated(inv);
    } catch (e) {
      toast.error(e.message || "Couldn't create the pass.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New visitor pass"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} disabled={!canSubmit} onClick={submit}>
            Create pass
          </Button>
        </>
      }
    >
      <div className="vsvp-form">
        <Input
          label="Guest name"
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="e.g. Amazon delivery, Ravi's cab"
          autoFocus
        />
        <div className="vsvp-kindrow">
          {[
            ["GUEST", "Guest"],
            ["DELIVERY", "Delivery"],
            ["CAB", "Cab"],
            ["SERVICE", "Service"],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`vsvp-kind ${kind === val ? "sel" : ""}`}
              onClick={() => setKind(val)}
            >
              {label}
            </button>
          ))}
        </div>
        <Input
          label="Guest phone (optional)"
          value={guestPhone}
          onChange={(e) => setGuestPhone(e.target.value)}
          placeholder="Optional"
          type="tel"
        />
        {villas.length > 1 ? (
          <label className="vsvp-field">
            <span className="vsvp-field-label">Villa</span>
            <select
              className="vsvp-select"
              value={villaId}
              onChange={(e) => setVillaId(e.target.value)}
            >
              <option value="">Select a villa…</option>
              {villas.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.number}
                </option>
              ))}
            </select>
          </label>
        ) : villas.length === 1 ? (
          <div className="vsvp-field-static">
            For <strong>{villas[0].number}</strong> · valid from now for
            24 hours.
          </div>
        ) : null}
        <label className="vsvp-check">
          <input
            type="checkbox"
            checked={multi}
            onChange={(e) => setMulti(e.target.checked)}
          />
          <span>Allow multiple entries</span>
        </label>
        {multi ? (
          <Input
            label="Max entries"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            helper="Between 2 and 100."
          />
        ) : null}
      </div>
    </Dialog>
  );
}

function PassDialog({ invite, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [invite]);

  const onShare = useMemo(
    () => async () => {
      if (!invite) return;
      const msg = shareMessage(invite);
      try {
        if (navigator.share) {
          await navigator.share({ title: "Visitor pass", text: msg });
          return;
        }
        await navigator.clipboard.writeText(msg);
        toast.success("Pass details copied.");
      } catch {
        /* user cancelled share — ignore */
      }
    },
    [invite],
  );

  const onCopyCode = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.short_code);
      setCopied(true);
      toast.success("Code copied.");
    } catch {
      toast.error("Couldn't copy the code.");
    }
  };

  return (
    <Dialog
      open={!!invite}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Visitor pass"
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      {invite ? (
        <div className="vsvp-pass">
          <div className="vsvp-pass-name">{invite.guest_name}</div>
          <div className="vsvp-pass-sub">
            {invite.villa_number} · valid until{" "}
            {fmtDateTime(invite.valid_to)}
          </div>
          <div className="vsvp-qr">
            <QRCodeSVG value={invite.code} size={216} level="M" />
          </div>
          <div className="vsvp-pass-or">or show this code at the gate</div>
          <div className="vsvp-code">{invite.short_code}</div>
          <div className="vsvp-pass-actions">
            <Button
              variant="secondary"
              leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
              onClick={onCopyCode}
            >
              {copied ? "Copied" : "Copy code"}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Share2 size={16} />}
              onClick={onShare}
            >
              Share
            </Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
